import { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  limit,
  orderBy,
} from "firebase/firestore";
import { parseISO, formatDistanceToNowStrict } from "date-fns";
import { db } from "../../utils/firebase";

import { MdRefresh} from 'react-icons/md';


const TodaysCompletedOrders = () => {
  const [groupedOrders, setGroupedOrders] = useState({});
  const [loadingCompleted, setLoadingCompleted] = useState(false);
  const restaurantEmail = localStorage.getItem("user");

  const fetchCompletedOrdersToday = async () => {
    setLoadingCompleted(true);
    try {
      const now = new Date();
      const IST_OFFSET_MINUTES = 330;

      const startIST = new Date(now);
      startIST.setUTCMinutes(startIST.getUTCMinutes() - IST_OFFSET_MINUTES);
      startIST.setUTCHours(0, 0, 0, 0);

      const endIST = new Date(startIST);
      endIST.setUTCHours(23, 59, 59, 999);

      const startISO = startIST.toISOString();
      const endISO = endIST.toISOString();

      const ordersRef = collection(db, "orders");
      const q = query(
        ordersRef,
        where("email", "==", restaurantEmail),
        where("createdDateTime", ">=", startISO),
        where("createdDateTime", "<=", endISO),
        orderBy('createdDateTime', 'desc'),
              limit(20)
      );

      const querySnapshot = await getDocs(q);
      let rawOrders = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Limit to last 50 orders by createdDateTime descending
      rawOrders.sort((a, b) => new Date(b.createdDateTime) - new Date(a.createdDateTime));
     

      const grouped = {};

      rawOrders.forEach((order) => {
        const parts = order.order?.split("|") || [];
        const itemsRaw = parts[0]?.replace("Items:", "").trim();
        const tableNumber = parts.find((p) => p.includes("Table:"))?.split(":")[1]?.trim();
        const phone = parts.find((p) => p.includes("Phone Number:"))?.split(":")[1]?.trim();
        const customerName = parts.find((p) => p.includes("Customer Name:"))?.split(":")[1]?.trim();
        const address = parts.find((p) => p.includes("Address:"))?.split(":")[1]?.trim();
        const total = parts.find((p) => p.includes("Total:"))?.match(/₹(\d+)/)?.[1];

        const isDelivery = !tableNumber || tableNumber.toLowerCase().includes("delivery");

        const groupKey = isDelivery
          ? `Delivery|${phone}|${customerName}`
          : `Table ${tableNumber}|${phone}|${customerName}`;

        if (!grouped[groupKey]) {
          grouped[groupKey] = {
            isDelivery,
            phone,
            customerName,
            tableNumber,
            address,
            orders: [],
            totalBill: 0,
          };
        }

        grouped[groupKey].orders.push({
          ...order,
          items: itemsRaw,
          createdDateTime: parseISO(order.createdDateTime),
        });

        grouped[groupKey].totalBill += total ? parseInt(total) : 0;
      });

      setGroupedOrders(grouped);
    } catch (error) {
      console.error("Error fetching completed orders:", error);
    } finally {
      setLoadingCompleted(false);
    }
  };

  useEffect(() => {
    if (restaurantEmail) {
      fetchCompletedOrdersToday();
    }
  }, [restaurantEmail]);

  const renderGroup = (groupKey, group) => (
    <div key={groupKey} className="border border-gray-200 rounded-lg p-3 mb-4 bg-white shadow-sm text-sm">
      <div className="flex justify-between items-center mb-1">
        <h3 className="font-semibold text-gray-800 text-sm">
          {group.isDelivery ? "Delivery Order" : `Table #${group.tableNumber}`}
        </h3>
        <span className="text-gray-600 text-xs">
          {group.customerName} ({group.phone})
        </span>
      </div>

      {group.orders.map((order, index) => (
        <div key={order.id} className="mb-1">
          <div className="text-xs text-gray-700 font-medium">
            Order {index + 1} - {formatDistanceToNowStrict(order.createdDateTime, { addSuffix: true })}
          </div>
          <div className="text-xs text-gray-600">{order.items}</div>
        </div>
      ))}

      {group.isDelivery && group.address && (
        <div className="mt-2 text-xs text-gray-700">
          <span className="font-medium">Address:</span> {group.address}
        </div>
      )}

      <div className="mt-2 font-semibold text-green-700 text-right text-sm">
        Total: ₹{group.totalBill}
      </div>
    </div>
  );

  const groupedArray = Object.entries(groupedOrders);

  return (
    <div className="w-full bg-white border border-gray-100 rounded-2xl shadow-md p-4">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-md font-bold text-gray-800">📊 Today's Orders</h2>
        <button
          onClick={fetchCompletedOrdersToday}
          className="text-gray-500 hover:text-gray-800"
          title="Refresh orders"
        >
          <MdRefresh size={18} className="inline-block" />
        </button>
      </div>

     {loadingCompleted ? (
  <div className="text-gray-400 text-sm">Loading...</div>
) : groupedArray.length === 0 ? (
  <div className="text-gray-400 text-sm">No completed orders today.</div>
) : (
  <div className="max-h-[500px] overflow-y-auto pr-1">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Dine-in Orders */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">🍽️ Dine-In Orders</h3>
        {groupedArray
          .filter(([_, group]) => !group.isDelivery)
          .map(([key, group]) => renderGroup(key, group))}
      </div>

      {/* Delivery Orders */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">🏍️ Delivery Orders</h3>
        {groupedArray
          .filter(([_, group]) => group.isDelivery)
          .map(([key, group]) => renderGroup(key, group))}
      </div>
    </div>
  </div>
)}

    </div>
  );
};

export default TodaysCompletedOrders;
